import {useState, useCallback, useMemo, useEffect} from 'react'
import {Dialog, DialogTitle, DialogContent, Box, Typography, IconButton, Pagination} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import {useTranslation} from 'react-i18next'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'
import {listBackgrounds} from '../../../common/api/generated/endpoints/assets/assets'
import type {PredefinedBackground} from '../../../common/api/generated/model'
import {handleApiError} from '../../../common/errors/handleApiError'
import LoadingLogo from '../LoadingLogo'

const PAGE_SIZE = 12

// ── Single background thumbnail ─────────────────────────────────

const BgThumbnail = ({
  item,
  selected,
  onSelect,
}: {
  item: PredefinedBackground
  selected: boolean
  onSelect: (item: PredefinedBackground) => void
}) => {
  const tokens = useAppTokens()

  const handleClick = useCallback(() => {
    onSelect(item)
  }, [item, onSelect])

  return (
    <Box onClick={handleClick} sx={buildThumbStyles(tokens, selected)}>
      <Box
        component="img"
        src={item.thumbnailUrl}
        alt=""
        sx={{
          width: '100%',
          aspectRatio: '3/2',
          objectFit: 'cover',
          display: 'block',
        }}
      />
    </Box>
  )
}

const buildThumbStyles = (tokens: ThemeTokens, selected: boolean) => ({
  cursor: 'pointer',
  borderRadius: '6px',
  overflow: 'hidden',
  border: selected ? `2px solid ${tokens.text.primary}` : '2px solid transparent',
  transition: 'border-color 0.15s',
  backgroundColor: tokens.accordion.border,
  '&:hover': {
    borderColor: selected ? tokens.text.primary : tokens.text.muted,
  },
})

// ── BgPresetModal ───────────────────────────────────────────────

const BgPresetModal = ({
  open,
  onClose,
  onSelect,
  currentKey,
}: {
  open: boolean
  onClose: () => void
  onSelect: (key: string, url: string) => void
  currentKey: string
}) => {
  const {t} = useTranslation()
  const tokens = useAppTokens()
  const sx = useMemo(() => buildStyles(tokens), [tokens])

  const [items, setItems] = useState<PredefinedBackground[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(false)

  const fetchPage = useCallback(async (p: number) => {
    setLoading(true)
    try {
      const res = await listBackgrounds({page: p, pageSize: PAGE_SIZE})
      setItems(res.data.items)
      setPage(res.data.page)
      setTotalPages(res.data.totalPages)
    } catch (err) {
      handleApiError(err, 'bg-preset-load')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (open) fetchPage(1)
  }, [open, fetchPage])

  const handlePageChange = useCallback(
    (_: unknown, p: number) => {
      fetchPage(p)
    },
    [fetchPage],
  )

  const handleSelect = useCallback(
    (item: PredefinedBackground) => {
      onSelect(item.key, item.url)
      onClose()
    },
    [onSelect, onClose],
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth slotProps={{paper: {sx: sx.paper}}}>
      <DialogTitle sx={sx.title}>
        {t('scene.background.presets')}
        <IconButton onClick={onClose} size="small" sx={sx.closeButton}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={sx.content}>
        {loading ? (
          <Box sx={sx.loadingBox}>
            <LoadingLogo size={32} />
          </Box>
        ) : items.length === 0 ? (
          <Typography sx={sx.emptyText}>{t('scene.background.noPresets')}</Typography>
        ) : (
          <Box sx={sx.grid}>
            {items.map((item) => (
              <BgThumbnail key={item.key} item={item} selected={item.key === currentKey} onSelect={handleSelect} />
            ))}
          </Box>
        )}
        {totalPages > 1 && (
          <Box sx={sx.paginationBox}>
            <Pagination count={totalPages} page={page} onChange={handlePageChange} size="small" />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}

// ── Styles ───────────────────────────────────────────────────────

const buildStyles = (tokens: ThemeTokens) => ({
  paper: {
    backgroundColor: tokens.sidebar.background,
    backgroundImage: 'none',
  },
  title: {
    fontSize: 14,
    fontWeight: 600,
    color: tokens.text.primary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
  },
  closeButton: {
    color: tokens.text.muted,
  },
  content: {
    padding: '0 16px 16px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '8px',
  },
  loadingBox: {
    display: 'flex',
    justifyContent: 'center',
    padding: '32px',
  },
  emptyText: {
    fontSize: 12,
    color: tokens.text.muted,
    textAlign: 'center' as const,
    padding: '24px',
  },
  paginationBox: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '12px',
  },
})

export default BgPresetModal
